import { useRef, useState } from "react";
import { ArrowDown, ArrowUp, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAdminContent } from "@/context/AdminContentContext";
import { Field, Panel, TextArea, TextInput } from "@/components/admin/Field";

const MAX = 24;
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

function GalleryUpload({ onAdd, count }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onChange = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX - count);
    if (!files.length) return;
    setBusy(true);
    let ok = 0;
    for (const file of files) {
      if (file.size > 20 * 1024 * 1024) { toast.error(`"${file.name}" excede 20 MB.`); continue; }
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/admin/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        onAdd({ id: uid(), url: data.url, caption: "" });
        ok += 1;
      } catch (err) {
        toast.error(errorMessage(err, `Não foi possível carregar "${file.name}".`));
      }
    }
    if (ok) toast.success(`${ok} imagem(ns) adicionada(s). Guarda e publica para aparecer no site.`);
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };
  return (
    <>
      <input ref={ref} id="gallery-files" type="file" multiple accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={onChange} disabled={count >= MAX} data-testid="gallery-file-input" />
      <label htmlFor="gallery-files" className={`btn-primary h-10 cursor-pointer ${busy || count >= MAX ? "pointer-events-none opacity-60" : ""}`} data-testid="gallery-upload-button">
        <Upload size={14} /> {busy ? "A carregar…" : "Carregar screenshots"}
      </label>
    </>
  );
}

function GalleryRow({ item, index, total, onChange, onMove, onRemove }) {
  return (
    <li className="flex gap-3 rounded-md border border-eden-border bg-eden-bg p-3" data-testid={`gallery-row-${index}`}>
      <img src={item.url} alt="" className="h-20 w-32 shrink-0 rounded-sm bg-eden-surface object-cover" />
      <div className="min-w-0 flex-1 space-y-2">
        <TextInput id={`gallery-caption-${index}`} value={item.caption} onChange={(v) => onChange({ caption: v })} maxLength={160} placeholder="Legenda (opcional)" data-testid={`gallery-caption-${index}`} />
        <p className="truncate font-mono text-[11px] text-eden-muted" title={item.url}>{item.url}</p>
      </div>
      <div className="flex flex-col gap-1">
        <button type="button" className="btn-ghost h-7 px-2" onClick={() => onMove(-1)} disabled={index === 0} aria-label="Subir" data-testid={`gallery-up-${index}`}><ArrowUp size={14} /></button>
        <button type="button" className="btn-ghost h-7 px-2" onClick={() => onMove(1)} disabled={index === total - 1} aria-label="Descer" data-testid={`gallery-down-${index}`}><ArrowDown size={14} /></button>
        <button type="button" className="btn-ghost h-7 px-2 text-red-400 hover:text-red-300" onClick={onRemove} aria-label="Remover da galeria" data-testid={`gallery-remove-${index}`}><Trash2 size={14} /></button>
      </div>
    </li>
  );
}

export function GalleryEditor() {
  const { draft, update } = useAdminContent();
  const m = draft.media;
  const items = m.gallery || [];
  const setItems = (next) => update("media", { gallery: next });
  const move = (i, dir) => {
    const next = [...items];
    const [it] = next.splice(i, 1);
    next.splice(i + dir, 0, it);
    setItems(next);
  };
  return (
    <Panel title="Galeria de screenshots" description={`Imagens da cidade mostradas na página inicial (máx. ${MAX}). A primeira e a quarta aparecem em destaque.`}>
      <Field label="Título da secção" id="gallery-title"><TextInput id="gallery-title" value={m.gallery_title || ""} onChange={(v) => update("media", { gallery_title: v })} maxLength={120} /></Field>
      <Field label="Texto de apoio" id="gallery-text"><TextArea id="gallery-text" value={m.gallery_text || ""} onChange={(v) => update("media", { gallery_text: v })} rows={2} maxLength={400} /></Field>
      <div className="flex flex-wrap items-center gap-3">
        <GalleryUpload count={items.length} onAdd={(it) => update("media", { gallery: [...(draft.media.gallery || []), it] })} />
        <span className="text-xs text-eden-muted" data-testid="gallery-count">{items.length} / {MAX} imagens</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-eden-muted" data-testid="gallery-empty">Ainda não há screenshots. A secção só aparece no site quando existir pelo menos uma imagem.</p>
      ) : (
        <ul className="space-y-2" data-testid="gallery-list">
          {items.map((it, i) => (
            <GalleryRow key={it.id} item={it} index={i} total={items.length} onChange={(patch) => setItems(items.map((x) => (x.id === it.id ? { ...x, ...patch } : x)))} onMove={(dir) => move(i, dir)} onRemove={() => setItems(items.filter((x) => x.id !== it.id))} />
          ))}
        </ul>
      )}
    </Panel>
  );
}
